import React, { useState } from 'react';
import {
  HiMail,
  HiLocationMarker,
  HiPhone,
} from 'react-icons/hi';
import { FaLinkedinIn, FaFacebookF } from 'react-icons/fa';
import { api } from '../services/api';

const contactInfo = [
  {
    icon: HiMail,
    label: 'Email',
    value: 'marinanaznin.ac@gmail.com',
    href: 'mailto:marinanaznin.ac@gmail.com',
  },
  {
    icon: HiPhone,
    label: 'Phone',
    value: '+447438887136',
    href: 'tel:++447438887136',
  },
  {
    icon: HiLocationMarker,
    label: 'Location',
    value: 'Fort William, Scotland',
    href: null,
  },
];

const socialLinks = [
  { icon: FaLinkedinIn, href: 'https://www.linkedin.com/in/marina-naznin-217555391/', label: 'LinkedIn' },
  { icon: FaFacebookF, href: 'https://www.facebook.com/ashmari.ashmari.3', label: 'Facebook' },
];

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [submitMessage, setSubmitMessage] = useState({ type: '', text: '' });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSubmitMessage({ type: '', text: '' });

    try {
      const result = await api.saveContact(formData);
      if (result.success) {
        setSubmitMessage({ type: 'success', text: 'Message sent successfully!' });
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setSubmitMessage({ type: 'error', text: result.message || 'Failed to send message' });
      }
    } catch (error) {
      setSubmitMessage({ type: 'error', text: 'Failed to send message. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="section-padding bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <span className="text-primary font-medium text-sm tracking-wider uppercase">
            Get In Touch
          </span>
          <h2 className="section-title mt-2">
            Let's <span className="text-primary">Connect</span>
          </h2>
          <p className="section-subtitle">
            Have a question or ready to get started? I'd love to hear from you.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-12">
          {/* Contact Info */}
          <div className="lg:col-span-2 space-y-8">
            <div>
              <h3 className="text-xl font-display font-bold text-text-dark mb-6">
                Contact Information
              </h3>
              <div className="space-y-5">
                {contactInfo.map((info, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-soft-pink flex items-center justify-center flex-shrink-0">
                      <info.icon className="text-primary text-xl" />
                    </div>
                    <div>
                      <p className="text-sm text-text-light">{info.label}</p>
                      {info.href ? (
                        <a
                          href={info.href}
                          className="text-text-dark font-medium hover:text-primary transition-colors"
                        >
                          {info.value}
                        </a>
                      ) : (
                        <p className="text-text-dark font-medium">
                          {info.value}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-display font-bold text-text-dark mb-4">
                Follow Me
              </h3>
              <div className="flex gap-3">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-11 h-11 rounded-full bg-soft-pink flex items-center justify-center text-primary hover:bg-primary hover:text-white transition-all duration-300"
                    aria-label={social.label}
                  >
                    <social.icon size={16} />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-3">
            <form
              onSubmit={handleSubmit}
              className="bg-cream rounded-3xl p-8 md:p-10"
            >
              <div className="grid sm:grid-cols-2 gap-5 mb-5">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-text-dark mb-2"
                  >
                    Your Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Type your name"
                    className="w-full px-4 py-3 rounded-xl bg-white border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all duration-300 text-sm"
                  />
                </div>
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-text-dark mb-2"
                  >
                    Your Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="Type your email"
                    className="w-full px-4 py-3 rounded-xl bg-white border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all duration-300 text-sm"
                  />
                </div>
              </div>
              <div className="mb-5">
                <label
                  htmlFor="subject"
                  className="block text-sm font-medium text-text-dark mb-2"
                >
                  Subject
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  placeholder="Type your subject"
                  className="w-full px-4 py-3 rounded-xl bg-white border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all duration-300 text-sm"
                />
              </div>
              <div className="mb-6">
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-text-dark mb-2"
                >
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  placeholder="Type your message"
                  className="w-full px-4 py-3 rounded-xl bg-white border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all duration-300 text-sm resize-none"
                />
              </div>
              {submitMessage.text && (
                <div
                  className={`mb-4 p-3 rounded-lg text-sm ${
                    submitMessage.type === 'success'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-red-100 text-red-700'
                  }`}
                >
                  {submitMessage.text}
                </div>
              )}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-primary text-white py-3.5 rounded-xl font-medium hover:bg-primary-dark transition-all duration-300 hover:shadow-lg hover:shadow-primary/25 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
